import DOMPurify from "dompurify";

const DEFAULTS = {
    currency: "USD",
    PublisherInfo: {
        name: "",
        email: "",
        phone: "",
        country: "",
        status: "",
        currency: "USD",
        publisherId: "",
        balance: 0,
        totalLeadsGenerated: 0,
        earnings: 0,
        isVerified: false,
        cboAllowed: false,
        role: "publisher",
        globalPostback: "",
        createdAt: "",
        isApprovedAsAdmin: false,
        payoutInfo: {},
        adminInfo: {
            name: "",
            email: "",
        },
        reports: {
            clicks: {
                today: 0,
                yesterday: 0,
                dayOverDayChange: 0,
            },
            conversions: {
                today: 0,
                yesterday: 0,
                dayOverDayChange: 0,
            },
            crRate: {
                today: 0,
                yesterday: 0,
                dayOverDayChange: 0,
            },
            revenue: {
                today: 0,
                yesterday: 0,
                dayOverDayChange: 0,
                currency: "USD",
            }
        }
    },
};

const ALLOWED_CURRENCIES = ["INR", "USD", "EUR"];

function safeParseJSON(value, fallback) {
    try {
        const parsed = JSON.parse(value);
        return typeof parsed === "object" && parsed !== null ? parsed : fallback;
    } catch {
        return fallback;
    }
}

function sanitizeString(value, fallback = "") {
    if (typeof value !== "string") return fallback;
    const clean = DOMPurify.sanitize(value.trim());
    return clean || fallback;
}

const isValidNumber = (v) =>
    v !== null && v !== undefined && !Number.isNaN(Number(v));

function validateToken(value) {
    const clean = sanitizeString(value);
    return clean !== "" ? clean : null;
}

function validateCurrency(value) {
    const clean = sanitizeString(value).toUpperCase();
    return ALLOWED_CURRENCIES.includes(clean) ? clean : DEFAULTS.currency;
}

function validatePublisherInfo(obj) {
    if (!obj || typeof obj !== "object") return DEFAULTS.PublisherInfo;

    const p = DEFAULTS.PublisherInfo;

    return {
        name: sanitizeString(obj.name, p.name),
        email: sanitizeString(obj.email, p.email),
        phone: sanitizeString(obj.phone, p.phone),
        country: sanitizeString(obj.country, p.country),
        status: sanitizeString(obj.status, p.status),
        currency: sanitizeString(obj.currency, p.currency),
        publisherId: sanitizeString(obj.publisherId, p.publisherId),
        balance: typeof obj.balance === "number" ? obj.balance : p.balance,
        totalLeadsGenerated:
            typeof obj.totalLeadsGenerated === "number"
                ? obj.totalLeadsGenerated
                : p.totalLeadsGenerated,
        earnings: typeof obj.earnings === "number" ? obj.earnings : p.earnings,
        isVerified: typeof obj.isVerified === "boolean" ? obj.isVerified : p.isVerified,
        cboAllowed: typeof obj.cboAllowed === "boolean" ? obj.cboAllowed : p.cboAllowed,
        role: sanitizeString(obj.role, p.role),
        globalPostback: sanitizeString(obj.globalPostback, p.globalPostback),
        createdAt: sanitizeString(obj.createdAt, p.createdAt),
        isApprovedAsAdmin:
            typeof obj.isApprovedAsAdmin === "boolean" ? obj.isApprovedAsAdmin : p.isApprovedAsAdmin,
        payoutInfo: {
            upiID: sanitizeString(obj?.payoutInfo?.upiID, p.payoutInfo.upiID),
        },
        adminInfo: {
            name: sanitizeString(obj?.adminInfo?.name, p.adminInfo.name),
            email: sanitizeString(obj?.adminInfo?.email, p.adminInfo.email),
        },
        reports: {
            clicks: {
                today: isValidNumber(obj?.reports?.clicks?.today)
                    ? Number(obj.reports.clicks.today)
                    : p.reports.clicks.today,

                yesterday: isValidNumber(obj?.reports?.clicks?.yesterday)
                    ? Number(obj.reports.clicks.yesterday)
                    : p.reports.clicks.yesterday,

                dayOverDayChange: isValidNumber(obj?.reports?.clicks?.dayOverDayChange)
                    ? Number(obj.reports.clicks.dayOverDayChange)
                    : p.reports.clicks.dayOverDayChange,
            },

            conversions: {
                today: isValidNumber(obj?.reports?.conversions?.today)
                    ? Number(obj.reports.conversions.today)
                    : p.reports.conversions.today,

                yesterday: isValidNumber(obj?.reports?.conversions?.yesterday)
                    ? Number(obj.reports.conversions.yesterday)
                    : p.reports.conversions.yesterday,

                dayOverDayChange: isValidNumber(obj?.reports?.conversions?.dayOverDayChange)
                    ? Number(obj.reports.conversions.dayOverDayChange)
                    : p.reports.conversions.dayOverDayChange,
            },

            crRate: {
                today: isValidNumber(obj?.reports?.crRate?.today)
                    ? Number(obj.reports.crRate.today)
                    : p.reports.crRate.today,

                yesterday: isValidNumber(obj?.reports?.crRate?.yesterday)
                    ? Number(obj.reports.crRate.yesterday)
                    : p.reports.crRate.yesterday,

                dayOverDayChange: isValidNumber(obj?.reports?.crRate?.dayOverDayChange)
                    ? Number(obj.reports.crRate.dayOverDayChange)
                    : p.reports.crRate.dayOverDayChange,
            },

            revenue: {
                today: isValidNumber(obj?.reports?.revenue?.today)
                    ? Number(obj.reports.revenue.today)
                    : p.reports.revenue.today,

                yesterday: isValidNumber(obj?.reports?.revenue?.yesterday)
                    ? Number(obj.reports.revenue.yesterday)
                    : p.reports.revenue.yesterday,

                dayOverDayChange: isValidNumber(obj?.reports?.revenue?.dayOverDayChange)
                    ? Number(obj.reports.revenue.dayOverDayChange)
                    : p.reports.revenue.dayOverDayChange,

                currency: sanitizeString(obj.reports.revenue.currency, p.reports.revenue.currency),
            },
        },
    };
}

export const storageManager = {
    getToken: () => {
        const raw = localStorage.getItem("aToken");
        return validateToken(raw);
    },
    setToken: (value) => {
        const clean = validateToken(value);
        if (clean) {
            localStorage.setItem("aToken", clean);
        } else {
            localStorage.removeItem("aToken");
        }
    },
    getCurrency: () => {
        const raw = localStorage.getItem("currency");
        return validateCurrency(raw);
    },
    setCurrency: (value) => {
        const clean = validateCurrency(value);
        localStorage.setItem("currency", clean);
    },

    getPublisherInfo: () => {
        const raw = localStorage.getItem("PublisherInfo");
        const parsed = safeParseJSON(raw, DEFAULTS.PublisherInfo);
        return validatePublisherInfo(parsed);
    },
    setPublisherInfo: (obj) => {
        const clean = validatePublisherInfo(obj);
        localStorage.setItem("PublisherInfo", JSON.stringify(clean));
    },

    removeItem: (key) => {
        localStorage.removeItem(key);
    },
};